import { useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { ROLE_LABELS, subtabAccessibleFor } from "../lib/modules";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import { Plus, RotateCcw, Trash2, Search, CalendarRange, Settings2, Pencil, Eye, Upload, Camera, RefreshCw, UserCheck, UserX, Clock, AlertTriangle, Users, Activity, LayoutGrid, List as ListIcon } from "lucide-react";
import { PageHeader } from "../components/GradientCard";
import ExtendedEmployeeFields, { fieldLabel, MANDATORY_FIELDS_LIST } from "../components/ExtendedEmployeeFields";
import UserActivityLogDialog from "../components/UserActivityLogDialog";

import { fmtDateStr } from "../lib/datetime";

const ROLES = ["employee", "hr_admin", "sales_admin", "sales_team", "accounts_admin", "dispatch_admin", "it_admin", "crm_manager", "documentation_team", "trainer", "master_admin"];
const GENDERS = ["Male", "Female", "Other", "Prefer not to say"];
const ALL_MODULES = [
  "hr_enrollment", "team_attendance", "hr_leaves", "hr_daily_report",
  "sales_crm_report", "sales_orders", "documentation",
  "accounts_invoices", "accounts_advances",
  "dispatch_orders",
  "it_assets", "it_tickets",
  "crm_clients",
  "documents", "trainers",
  // "announcements" removed — access is now governed by Editor Corner → Tab Access (Master Admin only).
  // "incentive_user"/"incentive_admin" removed — Incentive module is now open to everyone.
  "vendor_management", "product_issuance", "service_order_management",
];

const EMPTY_FORM = {
  name: "", email: "", role: "employee", department: "", designation: "",
  phone: "", employee_code: "", company: "", gender: "",
  date_of_joining: "", date_of_birth: "", whatsapp: "",
  employment_type: "", job_type: "", emp_status: "",
  // Extended profile fields
  father_name: "", marital_status: "", spouse_name: "",
  nationality: "Indian",
  reporting_manager_id: "", reporting_manager_name: "",
  permanent_address: "", correspondence_address: "", same_as_permanent: true,
  blood_group: "", pan_number: "", aadhaar_number: "", passport_no: "", voter_id: "",
  personal_email: "", personal_whatsapp: "", company_gmail: "", company_phone: "",
  account_number: "", ifsc_code: "", bank_name: "", probation_ends: "",
  emergency_contact_name: "", emergency_relation: "", emergency_contact_number: "",
  password: "", modules: [],
};

const ADD_NEW_COMPANY = "__add_new__";

// ---- Employee Card ---------------------------------------------------
// Compact identity card used by the "All Employees" tab when card view is
// active. Larger footprint (3 per row) — shows more identity fields at a
// glance and includes the Separate / Reinstate quick-actions.
function EmployeeCard({ e, readOnly, onView, onEdit, onActivity, onToggleActive, onResetPassword, onDelete, onSeparate, onReinstate }) {
  const initials = ((e.name || e.email || "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase());
  const sepStatus = e.separation_status || "";
  return (
    <div className="pk-card p-5 flex flex-col gap-3 hover:shadow-md transition-shadow" data-testid={`emp-card-${e.email}`}>
      <div className="flex items-start gap-3">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white font-bold flex items-center justify-center text-[17px] flex-shrink-0 shadow-sm">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-slate-900 dark:text-slate-100 text-[15px] truncate">{e.name || "—"}</div>
          <div className="text-[12px] text-slate-500 dark:text-slate-400 truncate mono">{e.email}</div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1">
            <span className="text-[10.5px] uppercase tracking-wider font-bold rounded-full border px-1.5 py-0.5 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-800 dark:text-indigo-200 border-indigo-200 dark:border-indigo-800">
              {ROLE_LABELS[e.role] || e.role}
            </span>
            {e.department && (
              <span className="text-[10.5px] uppercase tracking-wider font-bold rounded-full border px-1.5 py-0.5 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700">
                {e.department}
              </span>
            )}
            {sepStatus === "resigned" && (
              <span className="text-[10.5px] uppercase tracking-wider font-bold rounded-full border px-1.5 py-0.5 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-800">
                Resigned
              </span>
            )}
            {sepStatus === "terminated" && (
              <span className="text-[10.5px] uppercase tracking-wider font-bold rounded-full border px-1.5 py-0.5 bg-rose-50 dark:bg-rose-950/40 text-rose-800 dark:text-rose-200 border-rose-300 dark:border-rose-800">
                Terminated
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2.5 text-[12px] pt-1">
        <CardField label="Designation" value={e.designation} />
        <CardField label="Company" value={e.company} />
        <CardField label="Code" value={e.employee_code} mono />
        <CardField label="Phone" value={e.phone} mono />
        <CardField label="Reporting Manager" value={e.reporting_manager_name} />
        <CardField label="Nationality" value={e.nationality} />
        <CardField label="Date of Joining" value={e.date_of_joining} />
        {sepStatus ? (
          <CardField label="Separation Date" value={e.separation_date} />
        ) : (
          <CardField label="Job Type" value={e.job_type} />
        )}
      </div>

      {sepStatus && e.separation_reason && (
        <div className="text-[11px] text-slate-600 dark:text-slate-400 border-l-2 border-slate-300 dark:border-slate-600 pl-2 italic">
          &ldquo;{e.separation_reason}&rdquo;{e.separated_by_name ? ` — ${e.separated_by_name}` : ""}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1">
        {e.is_active ? (
          <Badge className="bg-green-100 text-green-700 hover:bg-green-100 font-medium text-[10px]">Active</Badge>
        ) : (
          <Badge variant="secondary" className="bg-slate-200 text-slate-700 text-[10px]">Disabled</Badge>
        )}
        {!e.profile_status?.complete && (
          <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100 text-[10px]">Profile Pending</Badge>
        )}
        {e.is_default_password && (
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]">Default Pwd</Badge>
        )}
      </div>

      {!readOnly && (
        <div className="flex items-center justify-end border-t border-slate-100 dark:border-slate-800 pt-3">
          <div className="flex items-center gap-1 flex-wrap justify-end">
            <button onClick={onView} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-indigo-700" title="View" data-testid={`emp-card-view-${e.email}`}><Eye size={13} /></button>
            <button onClick={onEdit} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-indigo-700" title="Edit" data-testid={`emp-card-edit-${e.email}`}><Pencil size={13} /></button>
            <button onClick={onActivity} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-indigo-700" title="Activity Log" data-testid={`emp-card-activity-${e.email}`}><Activity size={13} /></button>
            <button onClick={onResetPassword} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-amber-700" title="Reset password" data-testid={`emp-card-reset-${e.email}`}><RotateCcw size={13} /></button>
            {sepStatus ? (
              <button onClick={onReinstate} className="p-1.5 rounded-md text-slate-500 hover:bg-emerald-600 hover:text-white" title="Re-instate" data-testid={`emp-card-reinstate-${e.email}`}><UserCheck size={13} /></button>
            ) : (
              <button onClick={onSeparate} className="p-1.5 rounded-md text-slate-500 hover:bg-amber-600 hover:text-white" title="Separate (resign / terminate)" data-testid={`emp-card-separate-${e.email}`}><UserX size={13} /></button>
            )}
            <button onClick={onDelete} className="p-1.5 rounded-md text-slate-500 hover:bg-rose-600 hover:text-white" title="Delete" data-testid={`emp-card-delete-${e.email}`}><Trash2 size={13} /></button>
          </div>
        </div>
      )}
    </div>
  );
}

function CardField({ label, value, mono }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</div>
      <div className={`text-slate-800 dark:text-slate-200 truncate ${mono ? "mono" : ""} ${!value ? "text-slate-400 italic" : ""}`}>{value || "—"}</div>
    </div>
  );
}

// ---- Reporting Line ----------------------------------------------------
// Renders a searchable select of active employees. When picked, stores both
// `reporting_manager_id` and `reporting_manager_name` on the form so the
// name is denormalised alongside the id (avoids a lookup at read time).
function ReportingLine({ form, setForm, employees }) {
  // Every active, non-temp employee can be a reporting manager. Master
  // Admin is always eligible — even for themselves — because the org has
  // designated MA as an allowed self-reporting head.
  const options = (employees || [])
    .filter((e) => e.is_active !== false && !e.is_temp_account)
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const pickManager = (id) => {
    if (id === "__none__") {
      setForm((f) => ({ ...f, reporting_manager_id: "", reporting_manager_name: "" }));
      return;
    }
    const mgr = options.find((o) => o.id === id);
    setForm((f) => ({
      ...f,
      reporting_manager_id: id,
      reporting_manager_name: mgr?.name || "",
    }));
  };
  return (
    <div className="md:col-span-2 pt-2 border-t border-slate-100 dark:border-slate-800">
      <div className="pk-table-header mb-2">Reporting Line</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Reporting Manager">
          <Select value={form.reporting_manager_id || "__none__"} onValueChange={pickManager}>
            <SelectTrigger data-testid="emp-reporting-manager"><SelectValue placeholder="Select manager…" /></SelectTrigger>
            <SelectContent className="max-h-[280px] overflow-y-auto">
              <SelectItem value="__none__">— None —</SelectItem>
              {options.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.name} · {ROLE_LABELS[e.role] || e.role}
                  {e.department ? ` · ${e.department}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>
      <div className="text-[10.5px] text-slate-500 dark:text-slate-400 mt-1">
        Only Master Admin or HR Admin can assign a reporting manager.
      </div>
    </div>
  );
}

export default function Employees({ readOnly = false, embedded = false }) {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [useDefault, setUseDefault] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null);
  const [balanceEmp, setBalanceEmp] = useState(null);
  const [balanceForm, setBalanceForm] = useState({ casual: 0, sick: 0, earned: 0 });
  const [companies, setCompanies] = useState([]);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");

  // Filters
  const [filterCompany, setFilterCompany] = useState("__all__");
  const [filterRole, setFilterRole] = useState("__all__");
  const [filterStatus, setFilterStatus] = useState("__all__"); // active | disabled | pending | complete
  const [activeTab, setActiveTab] = useState("current"); // current | resigned | terminated | temp
  const [viewEmp, setViewEmp] = useState(null);
  const [activityEmp, setActivityEmp] = useState(null);
  const [separationEmp, setSeparationEmp] = useState(null);
  const [separationForm, setSeparationForm] = useState({ status: "resigned", reason: "", separation_date: "" });
  // Card | list toggle for the "All Employees" tab
  const [viewMode] = useGlobalViewMode(); // v109 — synced with the top-bar toggle

  // Auto-calc probation_ends = DOJ + 6 months
  const _calcProbationEnds = (doj) => {
    if (!doj) return "";
    try {
      const d = new Date(doj);
      d.setMonth(d.getMonth() + 6);
      return d.toISOString().slice(0, 10);
    } catch { return ""; }
  };
  useEffect(() => {
    if (form.date_of_joining) {
      const pe = _calcProbationEnds(form.date_of_joining);
      if (pe && pe !== form.probation_ends) setForm((f) => ({ ...f, probation_ends: pe }));
    }
  }, [form.date_of_joining]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCompanies = async () => {
    try {
      const { data } = await api.get("/companies");
      setCompanies(data || []);
    } catch (e) { /* ignore */ }
  };

  const load = async () => {
    try {
      // Pass include_temp=true so the Enrollment page (and only this page) can
      // surface candidate_temp / ex_employee_temp accounts in its Temp Accounts tab.
      const { data } = await api.get("/employees?include_temp=true");
      setEmployees(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  useEffect(() => { load(); loadCompanies(); }, []);

  // Split into regular vs temp accounts. Temp accounts come from Onboarding
  // (candidate_temp) or Offboarding (ex_employee_temp) flows.
  const TEMP_ROLES = ["candidate_temp", "ex_employee_temp"];
  const regularEmployees = employees.filter((e) => !TEMP_ROLES.includes(e.role));
  const tempEmployees = employees.filter((e) => TEMP_ROLES.includes(e.role));

  const _applyFilters = (list) => list.filter((e) => {
    const q = search.toLowerCase();
    if (q) {
      const hit = e.name?.toLowerCase().includes(q)
        || e.email?.toLowerCase().includes(q)
        || e.department?.toLowerCase().includes(q)
        || e.employee_code?.toLowerCase().includes(q)
        || e.company?.toLowerCase().includes(q);
      if (!hit) return false;
    }
    if (filterCompany !== "__all__" && e.company !== filterCompany) return false;
    if (filterRole !== "__all__" && e.role !== filterRole) return false;
    if (filterStatus === "active" && !e.is_active) return false;
    if (filterStatus === "disabled" && e.is_active) return false;
    if (filterStatus === "pending" && e.profile_status?.complete) return false;
    if (filterStatus === "complete" && !e.profile_status?.complete) return false;
    return true;
  });

  // ---- Bucket-first, then filter -----------------------------------
  // Step 1: split the whole directory into per-tab raw buckets so the
  // filter chips can offer options derived from just what's visible in
  // the current tab (roles/companies/etc that exist in that bucket).
  const rawBuckets = {
    current: regularEmployees.filter((e) => !e.separation_status && e.is_active !== false),
    inactive: regularEmployees.filter((e) => !e.separation_status && e.is_active === false),
    resigned: regularEmployees.filter((e) => e.separation_status === "resigned"),
    terminated: regularEmployees.filter((e) => e.separation_status === "terminated"),
    temp: tempEmployees,
  };
  const activeBucket = rawBuckets[activeTab] || [];
  // Step 2: apply search + filter chips inside the active bucket only
  const bucketForTab = _applyFilters(activeBucket);
  // Legacy variables kept so existing render code compiles
  const filtered = bucketForTab;
  const filteredTemp = bucketForTab;
  // Filter chip option universes — derived from the raw bucket so users
  // don't see filters for values that don't exist on the current tab.
  const availableRoles = Array.from(new Set(activeBucket.map((e) => e.role).filter(Boolean)));
  const availableCompanies = Array.from(new Set(activeBucket.map((e) => e.company).filter(Boolean))).sort();

  // Today's ISO for expiry comparison on temp accounts.
  const todayIso = new Date().toISOString().slice(0, 10);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (useDefault) delete payload.password;
      else if (!payload.password) {
        toast.error("Enter a password or use default");
        setSubmitting(false);
        return;
      }
      const { data } = await api.post("/employees", payload);
      toast.success("Employee added");
      setForm(EMPTY_FORM);
      setUseDefault(true);
      setOpen(false);
      setCreatedInfo({ email: data.user.email, password: data.generated_password });
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const resetPassword = async (id) => {
    try {
      const { data } = await api.post(`/employees/${id}/reset-password`, {});
      toast.success("Password reset");
      setCreatedInfo({ email: employees.find((e) => e.id === id)?.email, password: data.new_password });
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const toggleActive = async (id, current) => {
    try {
      await api.post(`/employees/${id}/toggle-active`, { is_active: !current });
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Employee deleted");
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const [editEmp, setEditEmp] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", department: "", designation: "", phone: "", whatsapp: "", role: "employee", modules: [], date_of_joining: "", date_of_birth: "", salary: "", employment_type: "", job_type: "" });
  const [restoreOpen, setRestoreOpen] = useState(false);

  const openEdit = (e) => {
    setEditEmp(e);
    setEditForm({
      name: e.name || "",
      department: e.department || "",
      designation: e.designation || "",
      phone: e.phone || "",
      whatsapp: e.whatsapp || "",
      role: e.role || "employee",
      modules: e.modules || [],
      date_of_joining: e.date_of_joining || "",
      date_of_birth: e.date_of_birth || "",
      salary: e.salary != null ? String(e.salary) : "",
      employment_type: e.employment_type || "",
      job_type: e.job_type || "",
      emp_status: e.emp_status || "",
      company: e.company || "",
      gender: e.gender || "",
      employee_code: e.employee_code || "",
      // Extended fields
      father_name: e.father_name || "",
      marital_status: e.marital_status || "",
      spouse_name: e.spouse_name || "",
      permanent_address: e.permanent_address || "",
      correspondence_address: e.correspondence_address || "",
      same_as_permanent: e.same_as_permanent !== false,
      blood_group: e.blood_group || "",
      pan_number: e.pan_number || "",
      aadhaar_number: e.aadhaar_number || "",
      passport_no: e.passport_no || "",
      voter_id: e.voter_id || "",
      personal_email: e.personal_email || "",
      personal_whatsapp: e.personal_whatsapp || "",
      company_gmail: e.company_gmail || "",
      company_phone: e.company_phone || "",
      account_number: e.account_number || "",
      ifsc_code: e.ifsc_code || "",
      bank_name: e.bank_name || "",
      probation_ends: e.probation_ends || "",
      emergency_contact_name: e.emergency_contact_name || "",
      emergency_relation: e.emergency_relation || "",
      emergency_contact_number: e.emergency_contact_number || "",
    });
  };

  const toggleEditModule = (_key) => {
    // no-op: module access UI removed (v96)
  };

  // Auto-calc probation_ends for the edit form when DOJ changes
  useEffect(() => {
    if (editForm.date_of_joining) {
      const pe = _calcProbationEnds(editForm.date_of_joining);
      if (pe && pe !== editForm.probation_ends) setEditForm((f) => ({ ...f, probation_ends: pe }));
    }
  }, [editForm.date_of_joining]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveEdit = async () => {
    try {
      await api.patch(`/employees/${editEmp.id}`, editForm);
      toast.success(`Updated ${editForm.name}`);
      setEditEmp(null);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const updateRole = async (id, role) => {
    try {
      await api.patch(`/employees/${id}`, { role });
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const openBalance = (e) => {
    setBalanceEmp(e);
    setBalanceForm({
      casual: e.leave_balance?.casual ?? 12,
      sick: e.leave_balance?.sick ?? 8,
      earned: e.leave_balance?.earned ?? 0,
    });
  };

  const saveSeparation = async () => {
    if (!separationEmp) return;
    try {
      await api.post(`/employees/${separationEmp.id}/separation`, {
        status: separationForm.status,
        reason: separationForm.reason,
        separation_date: separationForm.separation_date,
      });
      toast.success(`Marked as ${separationForm.status.toUpperCase()}`);
      setSeparationEmp(null);
      setSeparationForm({ status: "resigned", reason: "", separation_date: "" });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const reinstate = async (emp) => {
    if (!window.confirm(`Re-instate ${emp.name} as an active employee?`)) return;
    try {
      await api.post(`/employees/${emp.id}/separation`, { status: "active" });
      toast.success("Re-instated"); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const saveBalance = async () => {
    try {
      await api.patch(`/employees/${balanceEmp.id}/leave-balance`, {
        casual: Number(balanceForm.casual),
        sick: Number(balanceForm.sick),
        earned: Number(balanceForm.earned),
      });
      toast.success("Leave balance updated");
      setBalanceEmp(null);
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const toggleModule = (_key) => {
    // no-op: per-user module access UI removed (v96)
  };

  return (
    <div className={embedded ? "space-y-6" : "max-w-[1400px] space-y-6"}>
      {!embedded && (
        <PageHeader
        moduleKey="hr"
        tone="indigo"
        icon={Users}
        eyebrow="Master Admin"
        title={readOnly ? "Enrolled Employees" : "Employee Management"}
        subtitle={readOnly
          ? "Read-only view of all enrolled employees."
          : "Onboard team members, assign module access, reset passwords, or disable accounts."}
        actions={!readOnly && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              className="bg-gradient-to-br from-indigo-600 to-violet-700 hover:from-indigo-700 hover:to-violet-800 text-white h-10 shadow-sm"
              onClick={() => setOpen(true)}
              data-testid="add-employee-button">
              <Plus size={16} strokeWidth={1.75} className="mr-1.5" /> Add Employee
            </Button>
            {/* v109.9.168 — Restore Deleted (soft-deleted) employees is otherwise unreachable. */}
            <Button
              variant="outline"
              onClick={() => setRestoreOpen(true)}
              className="h-10 text-slate-700 border-slate-300 hover:bg-slate-50"
              data-testid="restore-deleted-button">
              <RotateCcw size={14} strokeWidth={1.75} className="mr-1.5" /> Restore Deleted
            </Button>
          </div>
        )}
      />
      )}

      {/* v109.9.143 — Extracted Dialog so it's mounted regardless of embedded/!embedded. */}
      {!readOnly && (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="heading">Add New Employee</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-3">
                <Field label="Full Name *">
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="emp-name" />
                </Field>
                <Field label="Email *">
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="emp-email" />
                </Field>
                <div className="md:col-span-2">
                  <Label className="text-[12px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Roles</Label>
                  <RoleChips
                    value={Array.isArray(form.roles) && form.roles.length ? form.roles : (form.role ? [form.role] : ["employee"])}
                    onChange={(nextRoles) => {
                      // Master admin picked → exclusive. Otherwise keep everything.
                      const isMaster = nextRoles.includes("master_admin");
                      const final = isMaster ? ["master_admin"] : nextRoles;
                      setForm({ ...form, roles: final, role: final[0] || "employee" });
                    }}
                  />
                </div>
                <Field label="Department">
                  <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="emp-department" />
                </Field>
                <Field label="Designation">
                  <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} data-testid="emp-designation" />
                </Field>
                <Field label="Phone">
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="emp-phone" />
                </Field>
                <Field label="Employee Code">
                  <Input value={form.employee_code} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} placeholder="auto-generated" data-testid="emp-code" />
                </Field>
                <Field label="Company">
                  <Select
                    value={form.company || ""}
                    onValueChange={(v) => {
                      if (v === ADD_NEW_COMPANY) {
                        setShowAddCompany(true);
                      } else {
                        setForm({ ...form, company: v });
                      }
                    }}
                  >
                    <SelectTrigger data-testid="emp-company"><SelectValue placeholder="Select company…" /></SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                      ))}
                      <SelectItem value={ADD_NEW_COMPANY} data-testid="emp-company-add-new">+ Add new company…</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Gender">
                  <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                    <SelectTrigger data-testid="emp-gender"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Date of Joining">
                  <Input type="date" value={form.date_of_joining || ""} onChange={(e) => setForm({ ...form, date_of_joining: e.target.value })} data-testid="emp-doj" />
                </Field>
                <Field label="Date of Birth">
                  <Input type="date" value={form.date_of_birth || ""} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="emp-dob" />
                </Field>
                <Field label="WhatsApp Contact">
                  <Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+91…" data-testid="emp-whatsapp" />
                </Field>

                {/* Extended profile fields */}
                <ExtendedEmployeeFields form={form} setForm={setForm} />

                <ReportingLine form={form} setForm={setForm} employees={employees} />

                <div className="md:col-span-2 pt-2 border-t border-slate-100">
                  <div className="flex items-center gap-3 mb-3">
                    <Switch checked={useDefault} onCheckedChange={setUseDefault} data-testid="emp-use-default-password" />
                    <span className="text-sm text-slate-700">
                      Use default password (<span className="mono">Admin@123</span>)
                    </span>
                  </div>
                  {!useDefault && (
                    <Field label="Custom Password">
                      <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters" data-testid="emp-password" />
                    </Field>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={submitting} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="emp-submit">
                  {submitting ? "Adding…" : "Add Employee"}
                </Button>
              </DialogFooter>
            </DialogContent>
        </Dialog>
      )}

      {createdInfo && (
        <div className="pk-card p-4 bg-green-50 border-green-200" data-testid="created-creds-banner">
          <div className="text-[11px] uppercase tracking-wider text-green-700 font-semibold mb-1">Share these credentials</div>
          <div className="text-sm">
            <span className="mono">{createdInfo.email}</span>
            {createdInfo.password && (
              <> · Password: <span className="mono font-semibold">{createdInfo.password}</span></>
            )}
          </div>
          <button onClick={() => setCreatedInfo(null)} className="text-xs text-green-700 underline mt-1">Dismiss</button>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <TabsList data-testid="emp-tabs">
            {subtabAccessibleFor(user, "hr", "enrolled", "current") && (
              <TabsTrigger value="current" data-testid="emp-tab-current">Current ({rawBuckets.current.length})</TabsTrigger>
            )}
            {subtabAccessibleFor(user, "hr", "enrolled", "inactive") && (
              <TabsTrigger value="inactive" data-testid="emp-tab-inactive">Inactive ({rawBuckets.inactive.length})</TabsTrigger>
            )}
            {subtabAccessibleFor(user, "hr", "enrolled", "resigned") && (
              <TabsTrigger value="resigned" data-testid="emp-tab-resigned">Resigned ({rawBuckets.resigned.length})</TabsTrigger>
            )}
            {subtabAccessibleFor(user, "hr", "enrolled", "terminated") && (
              <TabsTrigger value="terminated" data-testid="emp-tab-terminated">Terminated ({rawBuckets.terminated.length})</TabsTrigger>
            )}
            {subtabAccessibleFor(user, "hr", "enrolled", "temp") && (
              <TabsTrigger value="temp" data-testid="emp-tab-temp">
                Temp {rawBuckets.temp.length > 0 && (
                  <span className="ml-1.5 text-[10px] font-bold bg-violet-600 text-white rounded-full px-1.5 py-0.5">{rawBuckets.temp.length}</span>
                )}
              </TabsTrigger>
            )}
          </TabsList>
          {/* v109.9.143 — Embedded (HR module) "Add Employee" button. */}
          {embedded && !readOnly && (
            <Button
              className="bg-gradient-to-br from-indigo-600 to-violet-700 hover:from-indigo-700 hover:to-violet-800 text-white h-9 shadow-sm"
              onClick={() => setOpen(true)}
              data-testid="add-employee-button-embedded"
            >
              <Plus size={16} strokeWidth={1.75} className="mr-1.5" /> Add Employee
            </Button>
          )}
          {/* v109.9.168 — Restore Deleted (embedded HR shell) */}
          <Button
            variant="outline"
            onClick={() => setRestoreOpen(true)}
            className="h-9 text-slate-700 border-slate-300 hover:bg-slate-50"
            data-testid="restore-deleted-button-embedded"
          >
            <RotateCcw size={14} strokeWidth={1.75} className="mr-1.5" /> Restore Deleted
          </Button>
        </div>

        {/* Search + filter row — shared across both tabs */}
        <div className="pk-card p-3 mt-4 flex items-center gap-2 flex-wrap" data-testid="emp-filters">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, code, dept, company…" className="pl-9 h-9" data-testid="emp-search" />
          </div>

          {/* Status chips — options reduced to what's applicable to the
              currently-visible tab (Active/Disabled don't apply on tabs
              that are already single-state). Profile Pending/Complete
              always available. */}
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="filter-status-chips">
            {[
              { v: "__all__", label: "All" },
              // Active/Disabled distinction is meaningless on tabs that
              // are already scoped to one active state.
              ...(activeTab === "current" || activeTab === "inactive" ? [] : [
                { v: "active", label: "Active" },
                { v: "disabled", label: "Disabled" },
              ]),
              { v: "pending", label: "Profile Pending" },
              { v: "complete", label: "Profile Complete" },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setFilterStatus(opt.v)}
                className={`h-8 px-2.5 rounded-md text-[11.5px] font-medium border transition-colors ${
                  filterStatus === opt.v
                    ? "bg-[var(--pk-accent)] text-white border-[var(--pk-accent)]"
                    : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
                data-testid={`filter-status-${opt.v}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <Select value={filterCompany} onValueChange={setFilterCompany}>
              <SelectTrigger className="h-9 w-[160px] text-[12.5px]" data-testid="filter-company"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Companies ({availableCompanies.length})</SelectItem>
                {availableCompanies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="h-9 w-[150px] text-[12.5px]" data-testid="filter-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Roles ({availableRoles.length})</SelectItem>
                {availableRoles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}
              </SelectContent>
            </Select>
            {(search || filterCompany !== "__all__" || filterRole !== "__all__" || filterStatus !== "__all__") && (
              <Button variant="outline" size="sm" className="h-9 text-[11.5px]"
                onClick={() => { setSearch(""); setFilterCompany("__all__"); setFilterRole("__all__"); setFilterStatus("__all__"); }}
                data-testid="filter-clear">
                Clear
              </Button>
            )}
          </div>
        </div>

        <div className="text-[10.5px] text-slate-500 mt-2 mb-3">
          Showing <b data-testid="emp-count">{bucketForTab.length}</b> of {activeBucket.length}
        </div>

        <TabsContent value="current" className="mt-0" />
        <TabsContent value="inactive" className="mt-0" />
        <TabsContent value="resigned" className="mt-0" />
        <TabsContent value="terminated" className="mt-0" />
        <TabsContent value="temp" className="mt-0">
          {viewMode === "card" ? (
            <TempAccountsCards
              rows={filteredTemp}
              todayIso={todayIso}
              readOnly={readOnly}
              onToggleActive={(e) => toggleActive(e.id, e.is_active)}
              onResetPassword={(e) => resetPassword(e.id)}
              onView={(e) => setViewEmp(e)}
              onDelete={(e) => remove(e.id)}
            />
          ) : (
            <TempAccountsTable
              rows={filteredTemp}
              todayIso={todayIso}
              readOnly={readOnly}
              onToggleActive={(e) => toggleActive(e.id, e.is_active)}
              onResetPassword={(e) => resetPassword(e.id)}
              onView={(e) => setViewEmp(e)}
              onDelete={(e) => remove(e.id)}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Table — only shown on the "All Employees" tab */}
      {activeTab !== "temp" && viewMode === "list" && (
      <div className="pk-card overflow-x-auto" data-testid="emp-table-all">
        <table className="w-full text-sm responsive-table">
          <thead className="border-b border-slate-200 bg-slate-50/60">
            <tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Company</Th>
              <Th>Role</Th>
              <Th>Department</Th>
              <Th>Code</Th>
              <Th>Status</Th>
              {!readOnly && <Th className="text-right">Actions</Th>}
            </tr>
          </thead>
          <tbody>
            {bucketForTab.length === 0 && (
              <tr><td colSpan={readOnly ? 7 : 8} className="text-center py-12 text-slate-400 text-sm">No employees match your filters.</td></tr>
            )}
            {bucketForTab.map((e) => (
              <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`emp-row-${e.email}`}>
                <td className="px-4 py-3 font-semibold text-slate-900" data-label="Name">{e.name}</td>
                <td className="px-4 py-3 mono text-[12px] text-slate-600" data-label="Email">{e.email}</td>
                <td className="px-4 py-3 text-slate-700 text-[12.5px]" data-label="Company">{e.company || <span className="text-slate-400 italic">—</span>}</td>
                <td className="px-4 py-3" data-label="Role">
                  {readOnly ? (
                    <span className="text-xs">{ROLE_LABELS[e.role]}</span>
                  ) : (
                    <Select value={e.role} onValueChange={(v) => updateRole(e.id, v)}>
                      <SelectTrigger className="h-8 text-xs w-[140px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-600" data-label="Department">{e.department || "—"}</td>
                <td className="px-4 py-3 mono text-[11px] text-slate-500" data-label="Code">{e.employee_code}</td>
                <td className="px-4 py-3" data-label="Status">
                  <div className="flex flex-wrap items-center gap-1">
                    {e.separation_status === "resigned" && (
                      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 font-medium">Resigned</Badge>
                    )}
                    {e.separation_status === "terminated" && (
                      <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100 font-medium">Terminated</Badge>
                    )}
                    {!e.separation_status && e.is_active === false && (
                      <Badge variant="secondary" className="bg-slate-200 text-slate-700 font-medium">Inactive</Badge>
                    )}
                    {!e.separation_status && e.is_active !== false && (
                      <Badge className="bg-green-100 text-green-700 hover:bg-green-100 font-medium">Active</Badge>
                    )}
                    {!e.profile_status?.complete && (
                      <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100 text-[10px]" data-testid={`emp-pending-${e.email}`}>
                        Profile Pending
                      </Badge>
                    )}
                    {e.is_default_password && (
                      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]">Default Pwd</Badge>
                    )}
                  </div>
                </td>
                {!readOnly && (
                  <td className="px-4 py-3 text-right" data-label="Actions">
                    <div className="flex items-center justify-end gap-1 flex-nowrap whitespace-nowrap">
                      <button onClick={() => setViewEmp(e)} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-indigo-700" title="View" data-testid={`emp-view-${e.email}`}><Eye size={13} /></button>
                      <button onClick={() => openEdit(e)} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-indigo-700" title="Edit" data-testid={`emp-edit-${e.email}`}><Pencil size={13} /></button>
                      <button onClick={() => setActivityEmp(e)} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-indigo-700" title="Activity Log" data-testid={`emp-activity-${e.email}`}><Activity size={13} /></button>
                      <button onClick={() => resetPassword(e.id)} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-amber-700" title="Reset password" data-testid={`emp-reset-${e.email}`}><RotateCcw size={13} /></button>
                      {e.separation_status || e.is_active === false ? (
                        <button onClick={() => reinstate(e)} className="p-1.5 rounded-md text-slate-500 hover:bg-emerald-600 hover:text-white" title="Re-instate as Active" data-testid={`emp-reinstate-${e.email}`}><UserCheck size={13} /></button>
                      ) : (
                        <button onClick={() => setSeparationEmp(e)} className="p-1.5 rounded-md text-slate-500 hover:bg-amber-600 hover:text-white" title="Change status (Inactive / Resigned / Terminated)" data-testid={`emp-separate-${e.email}`}><UserX size={13} /></button>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button className="p-1.5 rounded-md text-slate-500 hover:bg-rose-600 hover:text-white" title="Delete" data-testid={`emp-delete-${e.email}`}>
                            <Trash2 size={13} />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {e.name}?</AlertDialogTitle>
                            <AlertDialogDescription>This will permanently remove their account and access.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(e.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* Card grid — 3 per row, larger cards */}
      {activeTab !== "temp" && viewMode === "card" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="emp-cards-all">
          {bucketForTab.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-400 text-sm">No employees match your filters.</div>
          )}
          {bucketForTab.map((e) => (
            <EmployeeCard
              key={e.id}
              e={e}
              readOnly={readOnly}
              onView={() => setViewEmp(e)}
              onEdit={() => openEdit(e)}
              onActivity={() => setActivityEmp(e)}
              onToggleActive={() => toggleActive(e.id, e.is_active)}
              onResetPassword={() => resetPassword(e.id)}
              onDelete={() => remove(e.id)}
              onSeparate={() => setSeparationEmp(e)}
              onReinstate={() => reinstate(e)}
            />
          ))}
        </div>
      )}

      {/* Edit Employee Dialog */}
      <Dialog open={!!editEmp} onOpenChange={(o) => !o && setEditEmp(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="emp-edit-dialog">
          <DialogHeader>
            <DialogTitle className="heading">Edit Employee — {editEmp?.name}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
            <Field label="Name *">
              <Input value={editForm.name} onChange={(ev) => setEditForm({ ...editForm, name: ev.target.value })} data-testid="emp-edit-name" />
            </Field>
            <Field label="Role *">
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="emp-edit-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Department">
              <Input value={editForm.department} onChange={(ev) => setEditForm({ ...editForm, department: ev.target.value })} data-testid="emp-edit-dept" />
            </Field>
            <Field label="Designation">
              <Input value={editForm.designation} onChange={(ev) => setEditForm({ ...editForm, designation: ev.target.value })} data-testid="emp-edit-designation" />
            </Field>
            <Field label="Phone">
              <Input value={editForm.phone} onChange={(ev) => setEditForm({ ...editForm, phone: ev.target.value })} data-testid="emp-edit-phone" />
            </Field>
            <Field label="WhatsApp">
              <Input value={editForm.whatsapp} onChange={(ev) => setEditForm({ ...editForm, whatsapp: ev.target.value })} data-testid="emp-edit-whatsapp" />
            </Field>
            <Field label="Employee Code">
              <Input value={editForm.employee_code} onChange={(ev) => setEditForm({ ...editForm, employee_code: ev.target.value })} className="mono" data-testid="emp-edit-code" />
            </Field>
            <Field label="Company *">
              <Select
                value={editForm.company || ""}
                onValueChange={(v) => {
                  if (v === ADD_NEW_COMPANY) {
                    setShowAddCompany(true);
                  } else {
                    setEditForm({ ...editForm, company: v });
                  }
                }}
              >
                <SelectTrigger data-testid="emp-edit-company"><SelectValue placeholder="Select company…" /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                  ))}
                  <SelectItem value={ADD_NEW_COMPANY} data-testid="emp-edit-company-add-new">+ Add new company…</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Gender">
              <Select value={editForm.gender || ""} onValueChange={(v) => setEditForm({ ...editForm, gender: v })}>
                <SelectTrigger data-testid="emp-edit-gender"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Date of Joining">
              <Input type="date" value={editForm.date_of_joining || ""} onChange={(ev) => setEditForm({ ...editForm, date_of_joining: ev.target.value })} data-testid="emp-edit-doj" />
            </Field>
            <Field label="Date of Birth">
              <Input type="date" value={editForm.date_of_birth || ""} onChange={(ev) => setEditForm({ ...editForm, date_of_birth: ev.target.value })} data-testid="emp-edit-dob" />
            </Field>
            <Field label="Monthly Salary (₹) — Master Admin only">
              <Input type="number" value={editForm.salary} onChange={(ev) => setEditForm({ ...editForm, salary: ev.target.value })} placeholder="0" className="bg-amber-50/30" data-testid="emp-edit-salary" />
            </Field>

            {/* Extended profile fields */}
            <ExtendedEmployeeFields form={editForm} setForm={setEditForm} />

            <ReportingLine form={editForm} setForm={setEditForm} employees={employees} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEmp(null)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="emp-edit-save">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Leave Balance Dialog */}
      <Dialog open={!!balanceEmp} onOpenChange={(o) => !o && setBalanceEmp(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="heading">Leave Balance — {balanceEmp?.name}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3 py-2">
            <Field label="Casual">
              <Input type="number" min="0" value={balanceForm.casual} onChange={(ev) => setBalanceForm({ ...balanceForm, casual: ev.target.value })} data-testid="balance-input-casual" />
            </Field>
            <Field label="Sick">
              <Input type="number" min="0" value={balanceForm.sick} onChange={(ev) => setBalanceForm({ ...balanceForm, sick: ev.target.value })} data-testid="balance-input-sick" />
            </Field>
            <Field label="Earned">
              <Input type="number" min="0" value={balanceForm.earned} onChange={(ev) => setBalanceForm({ ...balanceForm, earned: ev.target.value })} data-testid="balance-input-earned" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBalanceEmp(null)}>Cancel</Button>
            <Button onClick={saveBalance} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="balance-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Employee dialog (read-only) */}
      <ViewEmployeeDialog viewEmp={viewEmp} onClose={() => setViewEmp(null)} onOpenEdit={openEdit} />
      <UserActivityLogDialog open={!!activityEmp} employee={activityEmp} onClose={() => setActivityEmp(null)} />

      {/* Separation Dialog — mark as resigned or terminated */}
      <Dialog open={!!separationEmp} onOpenChange={(o) => { if (!o) { setSeparationEmp(null); setSeparationForm({ status: "resigned", reason: "", separation_date: "" }); } }}>
        <DialogContent className="max-w-md" data-testid="emp-separation-dialog">
          <DialogHeader>
            <DialogTitle className="heading flex items-center gap-2"><UserX size={16} /> Change status — {separationEmp?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-3">
            <Field label="Status Change *">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setSeparationForm((s) => ({ ...s, status: "inactive" }))}
                  className={`h-9 px-3.5 rounded-full border text-[12px] font-bold transition-all ${separationForm.status === "inactive" ? "bg-slate-700 text-white border-slate-700" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}
                  data-testid="emp-sep-inactive"
                >
                  Inactive
                </button>
                <button
                  type="button"
                  onClick={() => setSeparationForm((s) => ({ ...s, status: "resigned" }))}
                  className={`h-9 px-3.5 rounded-full border text-[12px] font-bold transition-all ${separationForm.status === "resigned" ? "bg-amber-600 text-white border-amber-600" : "bg-white border-slate-200 text-slate-700 hover:border-amber-400"}`}
                  data-testid="emp-sep-resigned"
                >
                  Resigned
                </button>
                <button
                  type="button"
                  onClick={() => setSeparationForm((s) => ({ ...s, status: "terminated" }))}
                  className={`h-9 px-3.5 rounded-full border text-[12px] font-bold transition-all ${separationForm.status === "terminated" ? "bg-rose-600 text-white border-rose-600" : "bg-white border-slate-200 text-slate-700 hover:border-rose-400"}`}
                  data-testid="emp-sep-terminated"
                >
                  Terminated
                </button>
              </div>
            </Field>
            {(separationForm.status === "resigned" || separationForm.status === "terminated") && (
              <Field label="Separation Date">
                <Input type="date" value={separationForm.separation_date} onChange={(e) => setSeparationForm((s) => ({ ...s, separation_date: e.target.value }))} data-testid="emp-sep-date" />
              </Field>
            )}
            <Field label="Reason">
              <Input value={separationForm.reason} onChange={(e) => setSeparationForm((s) => ({ ...s, reason: e.target.value }))} placeholder={separationForm.status === "resigned" ? "e.g. Moving to another company" : separationForm.status === "terminated" ? "e.g. Performance concerns" : "e.g. On long leave / temporary hold"} data-testid="emp-sep-reason" />
            </Field>
            <div className="text-[10.5px] text-slate-500 dark:text-slate-400">
              In all three cases the account is disabled and the employee cannot log in with their main ID. Resigned / Terminated employees are queued for the Offboarding Portal for letter generation. You can re-instate them later from the same tab.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSeparationEmp(null)}>Cancel</Button>
            <Button onClick={saveSeparation} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="emp-sep-save">
              <UserX size={12} className="mr-1.5" /> Confirm Change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Company dialog */}
      <Dialog open={showAddCompany} onOpenChange={setShowAddCompany}>
        <DialogContent className="max-w-md" data-testid="add-company-dialog">
          <DialogHeader>
            <DialogTitle className="heading">Add New Company</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-[12px] uppercase tracking-wider font-semibold text-slate-600">Company Name *</Label>
            <Input
              value={newCompanyName}
              onChange={(e) => setNewCompanyName(e.target.value)}
              placeholder="e.g. Panchakosh Ventures"
              className="mt-1.5"
              data-testid="new-company-name"
              autoFocus
            />
            <div className="text-[10.5px] text-slate-500 mt-2 italic">
              Master Admin / HR Admin can add new companies. The company will appear in the dropdown
              immediately for everyone. Letterheads can be uploaded later from Offboarding → Letterhead Settings.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddCompany(false); setNewCompanyName(""); }}>Cancel</Button>
            <Button
              className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)]"
              onClick={async () => {
                const name = newCompanyName.trim();
                if (!name) { toast.error("Company name is required"); return; }
                try {
                  const { data } = await api.post("/companies", { name });
                  toast.success(`Added '${data.name}'`);
                  await loadCompanies();
                  setForm((f) => ({ ...f, company: data.name }));
                  setEditForm((f) => ({ ...f, company: data.name }));
                  setShowAddCompany(false);
                  setNewCompanyName("");
                } catch (e) { toast.error(formatError(e)); }
              }}
              data-testid="new-company-submit"
            >
              Add Company
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Deleted Employees dialog — Date-range filter + per-row Restore */}
      <RestoreDeletedDialog
        open={restoreOpen}
        onOpenChange={setRestoreOpen}
        onChanged={load}
      />
    </div>
  );
}

/* =====================================================================
   RestoreDeletedDialog — list soft-deleted employees within a date range
   and let Master Admin restore (or purge) them.
   ===================================================================== */
function RestoreDeletedDialog({ open, onOpenChange, onChanged }) {
  const today = new Date();
  const defaultFrom = new Date(today);
  defaultFrom.setDate(defaultFrom.getDate() - 30);
  const fmt = (d) => d.toISOString().slice(0, 10);
  const [from, setFrom] = useState(fmt(defaultFrom));
  const [to, setTo] = useState(fmt(today));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees", {
        params: { only_deleted: true, deleted_from: from, deleted_to: to, include_temp: true },
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Failed to load deleted employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const restore = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/employees/${id}/restore`);
      toast.success("Employee restored — they can log in again");
      setRows((r) => r.filter((x) => x.id !== id));
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Restore failed");
    } finally { setBusyId(""); }
  };

  const purge = async (id, name) => {
    if (!window.confirm(`PERMANENTLY remove ${name}?  This cannot be undone.`)) return;
    setBusyId(id);
    try {
      await api.delete(`/employees/${id}/purge`);
      toast.success(`${name} permanently removed`);
      setRows((r) => r.filter((x) => x.id !== id));
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Purge failed");
    } finally { setBusyId(""); }
  };

  const setQuickRange = (days) => {
    const t = new Date();
    const f = new Date(t); f.setDate(f.getDate() - days);
    setFrom(fmt(f)); setTo(fmt(t));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="restore-deleted-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw size={16} className="text-emerald-600 dark:text-emerald-300" />
            Restore Deleted Employees
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-[1fr,1fr,auto] gap-2 items-end mb-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Deleted From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 h-9" data-testid="restore-from-date" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Deleted To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 h-9" data-testid="restore-to-date" />
          </div>
          <Button onClick={load} disabled={loading} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="restore-search-btn">
            {loading ? "Searching…" : "Search"}
          </Button>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {[7, 30, 60, 90, 180, 365].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setQuickRange(d)}
              className="text-[10.5px] font-bold px-2 py-1 rounded-full bg-slate-100 text-slate-700 hover:bg-emerald-100 hover:text-emerald-700 dark:bg-[hsl(var(--muted))] dark:text-slate-300 dark:hover:bg-emerald-500/20 dark:hover:text-emerald-300 transition-colors"
              data-testid={`restore-quick-${d}`}
            >
              Last {d}d
            </button>
          ))}
        </div>

        {rows.length === 0 && !loading && (
          <div className="text-center py-10 px-3 bg-slate-50 dark:bg-[hsl(var(--muted))] rounded-lg border border-dashed border-slate-200 dark:border-[hsl(var(--border))]">
            <RotateCcw size={28} className="mx-auto text-slate-300 dark:text-slate-600 mb-2" />
            <div className="text-[12.5px] font-semibold text-slate-600 dark:text-slate-300">No deleted employees in this date range</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">Try widening the range or use one of the quick chips above.</div>
          </div>
        )}

        {rows.length > 0 && (
          <div className="rounded-lg border border-slate-200 dark:border-[hsl(var(--border))] overflow-hidden">
            <table className="w-full text-[12px]">
              <thead className="bg-slate-50 dark:bg-[hsl(var(--muted))] text-slate-600 dark:text-slate-300">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Employee</th>
                  <th className="px-3 py-2 text-left font-semibold">Role</th>
                  <th className="px-3 py-2 text-left font-semibold">Deleted At</th>
                  <th className="px-3 py-2 text-left font-semibold">Deleted By</th>
                  <th className="px-3 py-2 text-right font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100 dark:border-[hsl(var(--border))]" data-testid={`restore-row-${e.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-slate-800 dark:text-slate-100">{e.name || "—"}</div>
                      <div className="text-[10.5px] text-slate-500 dark:text-slate-400">{e.email}{e.employee_code ? ` · ${e.employee_code}` : ""}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{e.role}</td>
                    <td className="px-3 py-2 text-slate-700 dark:text-slate-300 mono">
                      {e.deleted_at ? new Date(e.deleted_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{e.deleted_by_name || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        onClick={() => restore(e.id)}
                        disabled={busyId === e.id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-[11px] mr-1.5"
                        data-testid={`restore-do-${e.id}`}
                      >
                        <RotateCcw size={11} className="mr-1" /> Restore
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => purge(e.id, e.name)}
                        disabled={busyId === e.id}
                        className="h-7 text-[11px] text-rose-700 border-rose-200 hover:bg-rose-50 dark:text-rose-300 dark:border-rose-800 dark:hover:bg-rose-900/30"
                        data-testid={`restore-purge-${e.id}`}
                      >
                        <Trash2 size={11} className="mr-1" /> Purge
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[12px] uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

// v109.9.64 — RoleChips: multi-select chip picker. `master_admin` is
// exclusive — ticking it hides every other chip since Master Admin already
// covers all access.
function RoleChips({ value = [], onChange }) {
  const selectedSet = new Set(value);
  const isMaster = selectedSet.has("master_admin");
  const toggle = (r) => {
    const s = new Set(selectedSet);
    if (r === "master_admin") {
      onChange(isMaster ? [] : ["master_admin"]);
      return;
    }
    if (isMaster) return; // ignore other chip clicks while master is selected
    if (s.has(r)) s.delete(r); else s.add(r);
    onChange(Array.from(s));
  };
  return (
    <div className="mt-1.5 space-y-2" data-testid="emp-role-chips">
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => toggle("master_admin")}
          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11.5px] font-bold border transition-all ${isMaster
              ? "bg-amber-500 text-white border-amber-600 shadow-md shadow-amber-400/30"
              : "bg-white dark:bg-slate-900 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/40 hover:bg-amber-50 dark:hover:bg-amber-500/10"}`}
          data-testid="emp-role-chip-master_admin"
        >
          👑 Master Admin
        </button>
      </div>
      {!isMaster && (
        <div className="flex flex-wrap gap-1.5">
          {ROLES.filter((r) => r !== "master_admin").map((r) => {
            const on = selectedSet.has(r);
            return (
              <button
                key={r}
                type="button"
                onClick={() => toggle(r)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11.5px] font-semibold border transition-all ${on
                    ? "bg-indigo-600 text-white border-indigo-700 shadow-sm"
                    : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-700 hover:border-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"}`}
                data-testid={`emp-role-chip-${r}`}
              >
                {ROLE_LABELS[r] || r}
              </button>
            );
          })}
        </div>
      )}
      {isMaster && (
        <div className="text-[11px] text-amber-700 dark:text-amber-300 font-semibold">
          Master Admin covers every module — other role chips are hidden.
        </div>
      )}
      {!isMaster && value.length === 0 && (
        <div className="text-[11px] text-rose-600 dark:text-rose-300 font-semibold">Pick at least one role.</div>
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-left pk-table-header ${className}`}>{children}</th>;
}

// =================== View Employee (read-only) ===================
function ViewEmployeeDialog({ viewEmp, onClose, onOpenEdit }) {
  if (!viewEmp) return null;
  const e = viewEmp;
  const missing = e.profile_status?.missing || [];
  const initials = ((e.name || e.email || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase());

  return (
    <Dialog open={!!viewEmp} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="emp-view-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2">
            <Eye size={18} /> {e.name}
            {!e.profile_status?.complete && (
              <Badge className="bg-rose-100 text-rose-800 ml-1">Profile Pending</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Profile completion alert */}
        {!e.profile_status?.complete && (
          <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-[12px] text-rose-900" data-testid="view-pending-alert">
            <div className="font-semibold mb-1">Profile is incomplete — {missing.length} field(s) missing:</div>
            <div className="flex flex-wrap gap-1.5">
              {missing.map((m) => (
                <Badge key={m} className="bg-white border border-rose-300 text-rose-700 text-[10px]">{fieldLabel(m)}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* Avatar (initials) + key headers */}
        <div className="flex items-start gap-4 pt-2">
          <div className="shrink-0">
            <div className="w-24 h-24 rounded-md bg-gradient-to-br from-indigo-500 to-violet-600 text-white font-bold grid place-items-center text-2xl" data-testid="view-initials">
              {initials}
            </div>
          </div>
          <div className="flex-1 grid grid-cols-2 gap-3 text-[12.5px]">
            <V k="Employee ID" v={e.employee_code} mono />
            <V k="Company" v={e.company} />
            <V k="Designation" v={e.designation} />
            <V k="Department" v={e.department} />
            <V k="Role" v={e.role} />
            <V k="EMP Status" v={e.emp_status} />
          </div>
        </div>

        {/* Personal */}
        <ViewSection title="Personal Identity">
          <V k="Father's Name" v={e.father_name} />
          <V k="Gender" v={e.gender} />
          <V k="Marital Status" v={e.marital_status} />
          {["Married", "Divorced", "Widowed", "Separated"].includes(e.marital_status) && (
            <V k="Spouse Name" v={e.spouse_name} />
          )}
          <V k="Date of Birth" v={fmtDateStr(e.date_of_birth)} mono />
          <V k="Blood Group" v={e.blood_group} />
        </ViewSection>

        {/* Address */}
        <ViewSection title="Address">
          <V k="Permanent Address" v={e.permanent_address} wide />
          {!e.same_as_permanent && <V k="Correspondence Address" v={e.correspondence_address} wide />}
        </ViewSection>

        {/* Identity Numbers */}
        <ViewSection title="Identity Numbers">
          <V k="PAN Number" v={e.pan_number} mono />
          <V k="Aadhaar Number" v={e.aadhaar_number} mono />
          <V k="Passport No." v={e.passport_no || "—"} mono />
          <V k="Voter ID" v={e.voter_id || "—"} mono />
        </ViewSection>

        {/* Contact */}
        <ViewSection title="Contact">
          <V k="Personal Email" v={e.personal_email} mono />
          <V k="Personal WhatsApp" v={e.personal_whatsapp} mono />
          <V k="Company Email (Login)" v={e.email} mono />
          <V k="Company Gmail" v={e.company_gmail} mono />
          <V k="Company Phone" v={e.company_phone} mono />
        </ViewSection>

        {/* Bank */}
        <ViewSection title="Bank Account">
          <V k="Account Number" v={e.account_number} mono />
          <V k="IFSC Code" v={e.ifsc_code} mono />
          <V k="Bank Name" v={e.bank_name || "—"} />
        </ViewSection>

        {/* Employment */}
        <ViewSection title="Employment">
          <V k="Date of Joining" v={fmtDateStr(e.date_of_joining)} mono />
          <V k="Probation Ends" v={fmtDateStr(e.probation_ends)} mono />
          <V k="Job Type" v={e.job_type} />
          <V k="EMP Status" v={e.emp_status} />
        </ViewSection>

        {/* Emergency */}
        <ViewSection title="Emergency Contact">
          <V k="Name" v={e.emergency_contact_name} />
          <V k="Relation" v={e.emergency_relation} />
          <V k="Contact Number" v={e.emergency_contact_number} mono />
        </ViewSection>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button
            onClick={() => { onClose(); onOpenEdit(e); }}
            className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)]"
            data-testid="view-open-edit"
          >
            <Pencil size={12} className="mr-1.5" /> Edit Profile
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ViewSection({ title, children }) {
  return (
    <div className="border-t border-slate-100 pt-3 mt-2">
      <div className="text-[10.5px] uppercase tracking-[0.18em] text-violet-700 font-bold mb-2">{title}</div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-[12.5px]">{children}</div>
    </div>
  );
}

function V({ k, v, mono, wide }) {
  return (
    <div className={wide ? "col-span-2 md:col-span-3" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k}</div>
      <div className={`text-slate-800 mt-0.5 ${mono ? "mono" : ""} ${!v ? "text-slate-400 italic" : ""}`}>{v || "—"}</div>
    </div>
  );
}

// =================== Photo Upload (Admin) — REMOVED (v97)
// Profile photos have been disabled across Employee Management and
// Directory. The upload widget is gone; users see initials-based avatars
// only. The backend endpoints and GridFS bucket entries were cleaned up
// in a one-time migration.
// ===============================================================

function StatTile({ icon: Icon, label, value, tone = "slate", testid }) {
  const toneMap = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
  };
  const iconTone = {
    slate: "text-slate-500",
    emerald: "text-emerald-600",
    rose: "text-rose-600",
    amber: "text-amber-600",
    violet: "text-violet-600",
  };
  return (
    <div className={`rounded-lg border px-3 py-2.5 flex items-center gap-2.5 ${toneMap[tone]}`} data-testid={testid}>
      <Icon size={18} strokeWidth={1.6} className={iconTone[tone]} />
      <div className="leading-tight">
        <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
        <div className="text-lg font-bold mono">{value}</div>
      </div>
    </div>
  );
}

function TempAccountsTable({ rows, todayIso, readOnly, onToggleActive, onResetPassword, onView, onDelete }) {
  const tempType = (e) => {
    if (e.role === "candidate_temp") return { label: "Onboarding Candidate", cls: "bg-violet-100 text-violet-700" };
    if (e.role === "ex_employee_temp") return { label: "Ex-Employee", cls: "bg-amber-100 text-amber-800" };
    return { label: e.role, cls: "bg-slate-100 text-slate-700" };
  };
  const expiryStatus = (iso) => {
    if (!iso) return null;
    const d = String(iso).slice(0, 10);
    if (d < todayIso) return { label: "Expired", cls: "bg-rose-100 text-rose-700" };
    // within 5 days?
    const t = new Date(d + "T00:00:00").getTime();
    const today = new Date(todayIso + "T00:00:00").getTime();
    const daysLeft = Math.round((t - today) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 5) return { label: `${daysLeft}d left`, cls: "bg-amber-100 text-amber-800" };
    return { label: `${daysLeft}d left`, cls: "bg-emerald-100 text-emerald-700" };
  };
  return (
    <div className="pk-card overflow-x-auto" data-testid="emp-table-temp">
      <table className="w-full text-sm responsive-table">
        <thead className="border-b border-slate-200 bg-slate-50/60">
          <tr>
            <Th>Name</Th>
            <Th>Email (Temp)</Th>
            <Th>Type</Th>
            <Th>Created</Th>
            <Th>Expires</Th>
            <Th>Active</Th>
            {!readOnly && <Th className="text-right">Actions</Th>}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={readOnly ? 6 : 7} className="text-center py-12 text-slate-400 text-sm" data-testid="emp-temp-empty">
              No temporary accounts match your filters.
            </td></tr>
          )}
          {rows.map((e) => {
            const t = tempType(e);
            const expIso = e.temp_access_expires_at;
            const exp = expiryStatus(expIso);
            return (
              <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`emp-temp-row-${e.email}`}>
                <td className="px-4 py-3 font-semibold text-slate-900" data-label="Name">{e.name || "—"}</td>
                <td className="px-4 py-3 mono text-[12px] text-slate-600" data-label="Email">{e.email}</td>
                <td className="px-4 py-3" data-label="Type">
                  <Badge className={`${t.cls} hover:${t.cls} text-[10.5px]`} data-testid={`emp-temp-type-${e.email}`}>{t.label}</Badge>
                </td>
                <td className="px-4 py-3 mono text-[11px] text-slate-500" data-label="Created">
                  {e.created_at ? String(e.created_at).slice(0, 10) : "—"}
                </td>
                <td className="px-4 py-3 mono text-[11px]" data-label="Expires">
                  {expIso ? (
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-700">{String(expIso).slice(0, 10)}</span>
                      {exp && <Badge className={`${exp.cls} hover:${exp.cls} text-[9.5px]`} data-testid={`emp-temp-exp-${e.email}`}>{exp.label}</Badge>}
                    </div>
                  ) : "—"}
                </td>
                <td className="px-4 py-3" data-label="Active">
                  {e.is_active ? (
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Active</Badge>
                  ) : (
                    <Badge variant="secondary" className="bg-slate-200 text-slate-700">Disabled</Badge>
                  )}
                </td>
                {!readOnly && (
                  <td className="px-4 py-3 text-right" data-label="Actions">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      <Switch checked={e.is_active} onCheckedChange={() => onToggleActive(e)} data-testid={`emp-temp-toggle-${e.email}`} />
                      <Button size="sm" variant="outline" onClick={() => onView(e)} className="h-8 text-xs" data-testid={`emp-temp-view-${e.email}`}>
                        <Eye size={12} className="mr-1" /> View
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onResetPassword(e)} className="h-8 text-xs" data-testid={`emp-temp-reset-${e.email}`}>
                        <RotateCcw size={12} className="mr-1" /> Reset
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`emp-temp-delete-${e.email}`}>
                            <Trash2 size={12} />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {e.name || e.email}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently remove this temp account and revoke its access. The linked
                              Onboarding / Offboarding record will remain intact.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => onDelete(e)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}




/* v109 — Card variant of TempAccountsTable so the top-bar Card view works on the Temp tab too. */
function TempAccountsCards({ rows, todayIso, readOnly, onToggleActive, onResetPassword, onView, onDelete }) {
  const tempType = (e) => {
    if (e.role === "candidate_temp") return { label: "Onboarding Candidate", cls: "bg-violet-100 text-violet-700 ring-violet-200" };
    if (e.role === "ex_employee_temp") return { label: "Ex-Employee", cls: "bg-amber-100 text-amber-800 ring-amber-200" };
    return { label: e.role, cls: "bg-slate-100 text-slate-700 ring-slate-200" };
  };
  const expiryStatus = (iso) => {
    if (!iso) return null;
    const d = String(iso).slice(0, 10);
    if (d < todayIso) return { label: "Expired", cls: "bg-rose-100 text-rose-700" };
    const t = new Date(d + "T00:00:00").getTime();
    const today = new Date(todayIso + "T00:00:00").getTime();
    const daysLeft = Math.round((t - today) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 5) return { label: `${daysLeft}d left`, cls: "bg-amber-100 text-amber-800" };
    return { label: `${daysLeft}d left`, cls: "bg-emerald-100 text-emerald-700" };
  };
  if (rows.length === 0) {
    return <div className="pk-card text-center py-12 text-slate-400 text-sm" data-testid="emp-cards-temp-empty">No temporary accounts match your filters.</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="emp-cards-temp">
      {rows.map((e) => {
        const t = tempType(e);
        const exp = expiryStatus(e.temp_access_expires_at);
        return (
          <div key={e.id} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 flex flex-col gap-2.5 transition-colors hover:border-violet-300 dark:hover:border-violet-500/40" data-testid={`emp-card-temp-${e.email}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-bold text-slate-900 dark:text-slate-100 truncate">{e.name || "—"}</div>
                <div className="mono text-[11px] text-slate-500 truncate">{e.email}</div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <Badge className={`${t.cls} text-[10px] ring-1`} data-keep-color>{t.label}</Badge>
                {e.is_active ? (
                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px]" data-keep-color>Active</Badge>
                ) : (
                  <Badge className="bg-slate-200 text-slate-700 text-[10px]" data-keep-color>Disabled</Badge>
                )}
              </div>
            </div>
            <div className="border-t border-slate-100 dark:border-slate-800 pt-2 grid grid-cols-2 gap-2 text-[11.5px]">
              <div>
                <div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">Created</div>
                <div className="mono text-slate-800 dark:text-slate-100">{e.created_at ? String(e.created_at).slice(0, 10) : "—"}</div>
              </div>
              <div>
                <div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">Expires</div>
                <div className="mono text-slate-800 dark:text-slate-100 flex items-center gap-1.5 flex-wrap">
                  {e.temp_access_expires_at ? String(e.temp_access_expires_at).slice(0, 10) : "—"}
                  {exp && <Badge className={`${exp.cls} text-[9.5px]`} data-keep-color>{exp.label}</Badge>}
                </div>
              </div>
            </div>
            {!readOnly && (
              <div className="flex items-center justify-end gap-1.5 flex-wrap pt-2 border-t border-slate-100 dark:border-slate-800">
                <Switch checked={e.is_active} onCheckedChange={() => onToggleActive(e)} data-testid={`emp-temp-card-toggle-${e.email}`} />
                <Button size="sm" variant="outline" onClick={() => onView(e)} className="h-8 text-xs" data-testid={`emp-temp-card-view-${e.email}`}>
                  <Eye size={12} className="mr-1" /> View
                </Button>
                <Button size="sm" variant="outline" onClick={() => onResetPassword(e)} className="h-8 text-xs" data-testid={`emp-temp-card-reset-${e.email}`}>
                  <RotateCcw size={12} className="mr-1" /> Reset
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`emp-temp-card-delete-${e.email}`}>
                      <Trash2 size={12} />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {e.name || e.email}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently remove this temp account and revoke its access. The linked
                        Onboarding / Offboarding record will remain intact.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => onDelete(e)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
